<template>
  <div class="pin-input">
    <div class="pin-input__digits">
      <div
        v-for="(position, index) in pinLength"
        :key="position"
        class="pin-input__digit-wrapper"
      >
        <input
          ref="inputs"
          v-model="digits[index]"
          type="text"
          inputmode="numeric"
          class="pin-input__digit"
          :class="{
            'pin-input__digit--filled': digits[index],
            'pin-input__digit--error': hasError,
          }"
          maxlength="1"
          @input="(e) => handleDigitInput(e, index)"
          @keydown="(e) => handleKeyDown(e, index)"
          @paste="handlePaste"
          @focus="() => handleFocus(index)"
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref, watch, onMounted,
} from 'vue';
import { Lengths, EMPTY_DIGITS } from '@common/types/auth';

interface Props {
  pinLength?: number;
  hasError?: boolean;
}

const {
  pinLength = Lengths.CODE,
  hasError = false,
} = defineProps<Props>();

const model = defineModel<string>({
  default: '',
});

const inputs = ref<(HTMLInputElement | null)[]>([]);
const digits = ref<string[]>([...EMPTY_DIGITS]);

watch(() => model.value, (newValue: string) => {
  if (!newValue) {
    digits.value = [...EMPTY_DIGITS];
    return;
  }

  const values = newValue.split('');
  digits.value = EMPTY_DIGITS.map((_, index) => values[index] || '');
}, { immediate: true });

watch(digits, (newDigits: string[]) => {
  const value = newDigits.join('');
  model.value = value;
}, { deep: true });

const focusInput = (index: number): void => {
  if (!inputs.value[index]) return;
  inputs.value[index]?.focus();
};

const handleDigitInput = (event: Event, index: number): void => {
  const target = event.target as HTMLInputElement;
  const digit = target.value.replace(/\D/g, '');
  digits.value[index] = digit;

  if (digit && index < pinLength - 1) {
    focusInput(index + 1);
  }
};

const handleKeyDown = (event: KeyboardEvent, index: number): void => {
  if (event.key === 'Backspace') {
    if (!digits.value[index] && index > 0) {
      digits.value[index - 1] = '';
      focusInput(index - 1);
    }
  }

  if (event.key === 'ArrowLeft' && index > 0) {
    focusInput(index - 1);
  }

  if (event.key === 'ArrowRight' && index < pinLength - 1) {
    focusInput(index + 1);
  }
};

const handlePaste = (event: ClipboardEvent): void => {
  event.preventDefault();
  const pastedText = event.clipboardData?.getData('text')
    .replace(/\D/g, '')
    .slice(0, pinLength) || '';

  pastedText.split('').forEach((digit, index) => {
    if (index < pinLength) {
      digits.value[index] = digit;
    }
  });

  const focusIndex = Math.min(pastedText.length, pinLength - 1);
  focusInput(focusIndex);
};

const handleFocus = (index: number): void => {
  if (inputs.value[index]) {
    inputs.value[index]?.select();
  }
};

onMounted(() => {
  focusInput(0);
});
</script>

<style lang="scss" scoped>
.pin-input {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;

  &__digits {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__digit-wrapper {
    display: flex;
  }

  &__digit {
    width: 4.6rem;
    height: 6.69rem;
    padding: 0;
    font-size: 3.6rem;
    font-weight: 700;
    text-align: center;
    border: 0.1rem solid $color-gray;
    border-radius: 0.51rem;
    outline: none;
    transition: all 0.3s;
    caret-color: $color-black;

    &:focus {
      border-color: $color-black;
      transform: scale(1.05);
    }

    &--filled {
      border-color: $color-black;
    }

    &--error {
      border-color: $color-sirena-wrong;
      color: $color-sirena-wrong;
      animation: shake 0.3s;
    }
  }

  &__error {
    font-size: 1.4rem;
    color: $color-sirena-wrong;
    text-align: center;
  }
}
</style>
